import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import type { Policy } from '../../services/policiesApi';
import type { Zone } from '../../services/zonesApi';

type SeverityOption = {
  value: string;
  label: string;
};

type OverrideSeverityValue = '' | 'low' | 'medium' | 'high' | 'critical';

type SnackbarState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning';
  autoHideDuration?: number;
};

type ZoneOverlay =
  | { kind: 'rect'; x: number; y: number; w: number; h: number }
  | { kind: 'polygon'; points: string }
  | null;

type PolicyAuxDialogsProps = {
  overrideOpen: boolean;
  closeOverride: () => void;
  overridePolicy: Policy | null;
  overrideDisable: boolean;
  setOverrideDisable: (value: boolean) => void;
  overrideSeverity: OverrideSeverityValue;
  setOverrideSeverity: (value: OverrideSeverityValue) => void;
  overrideCooldownSeconds: string;
  setOverrideCooldownSeconds: (value: string) => void;
  overrideDurationMinutes: number;
  setOverrideDurationMinutes: (value: number) => void;
  overrideReason: string;
  setOverrideReason: (value: string) => void;
  overrideSaving: boolean;
  canWritePolicies: boolean;
  severityOptions: SeverityOption[];
  submitOverride: () => void;
  deleteAllDeviceOpen: boolean;
  closeDeleteAllDevice: () => void;
  deleteAllDeviceSaving: boolean;
  deleteAllDeviceId: string;
  setDeleteAllDeviceId: (value: string) => void;
  devicesWithPolicies: Array<{ deviceId: string; count: number }>;
  handleDeleteAllPoliciesForDevice: () => void;
  zonePreviewOpen: boolean;
  closeZonePreview: () => void;
  zonePreviewZone: Zone | null;
  zonePreviewImg: string | null;
  zonePreviewLoading: boolean;
  zonePreviewError: string | null;
  zoneOverlay: ZoneOverlay;
  deployLintOpen: boolean;
  closeDeployLint: () => void;
  deployLintTitle: string;
  deployLintErrors: any[];
  lintFixHref: (error: any) => string | null;
  openLintFix: (href: string) => void;
  clockGateOpen: boolean;
  closeClockGate: () => void;
  clockGatePolicy: string;
  clockGateDevices: string[];
  openClockPreflight: () => void;
  snackbar: SnackbarState;
  closeSnackbar: () => void;
};

const PolicyAuxDialogs: React.FC<PolicyAuxDialogsProps> = ({
  overrideOpen,
  closeOverride,
  overridePolicy,
  overrideDisable,
  setOverrideDisable,
  overrideSeverity,
  setOverrideSeverity,
  overrideCooldownSeconds,
  setOverrideCooldownSeconds,
  overrideDurationMinutes,
  setOverrideDurationMinutes,
  overrideReason,
  setOverrideReason,
  overrideSaving,
  canWritePolicies,
  severityOptions,
  submitOverride,
  deleteAllDeviceOpen,
  closeDeleteAllDevice,
  deleteAllDeviceSaving,
  deleteAllDeviceId,
  setDeleteAllDeviceId,
  devicesWithPolicies,
  handleDeleteAllPoliciesForDevice,
  zonePreviewOpen,
  closeZonePreview,
  zonePreviewZone,
  zonePreviewImg,
  zonePreviewLoading,
  zonePreviewError,
  zoneOverlay,
  deployLintOpen,
  closeDeployLint,
  deployLintTitle,
  deployLintErrors,
  lintFixHref,
  openLintFix,
  clockGateOpen,
  closeClockGate,
  clockGatePolicy,
  clockGateDevices,
  openClockPreflight,
  snackbar,
  closeSnackbar,
}) => {
  return (
    <>
      <Dialog open={overrideOpen} onClose={closeOverride} maxWidth="sm" fullWidth>
        <DialogTitle>Operator override</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Overrides are time-bound and auto-deploy to edge. Use for maintenance/temporary tuning.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Target: <b>{overridePolicy?.name || overridePolicy?.policy_id || '—'}</b>
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={overrideDisable} onChange={(e) => setOverrideDisable(e.target.checked)} />}
            label="Disable this policy temporarily"
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Override severity (optional)</InputLabel>
            <Select
              label="Override severity (optional)"
              value={overrideSeverity}
              onChange={(e) => setOverrideSeverity((String(e.target.value || '') as OverrideSeverityValue))}
            >
              <MenuItem value="">(no change)</MenuItem>
              {severityOptions.map((severity) => (
                <MenuItem key={severity.value} value={severity.value}>
                  {severity.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Override cooldown_seconds (optional)"
            value={overrideCooldownSeconds}
            onChange={(e) => setOverrideCooldownSeconds(e.target.value)}
            placeholder="e.g. 300"
          />
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Duration (minutes)"
            type="number"
            value={overrideDurationMinutes}
            onChange={(e) => setOverrideDurationMinutes(Number(e.target.value || 0))}
          />
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Reason"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOverride}>Cancel</Button>
          <Button variant="contained" onClick={submitOverride} disabled={!overridePolicy || overrideSaving || !canWritePolicies}>
            {overrideSaving ? 'Applying…' : 'Apply override'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteAllDeviceOpen} onClose={() => !deleteAllDeviceSaving && closeDeleteAllDevice()} maxWidth="sm" fullWidth>
        <DialogTitle>Delete all policies on device</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will soft-delete all policies targeting the selected device. Deploy after to push the change to the edge.
          </Alert>
          <FormControl fullWidth>
            <InputLabel>Device</InputLabel>
            <Select
              label="Device"
              value={deleteAllDeviceId}
              onChange={(e) => setDeleteAllDeviceId(String(e.target.value || ''))}
            >
              {devicesWithPolicies.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.deviceId} ({device.count} policies)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteAllDevice} disabled={deleteAllDeviceSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAllPoliciesForDevice}
            disabled={!deleteAllDeviceId || deleteAllDeviceSaving || !canWritePolicies}
            startIcon={deleteAllDeviceSaving ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteAllDeviceSaving ? 'Deleting…' : 'Delete all'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={zonePreviewOpen} onClose={closeZonePreview} maxWidth="md" fullWidth>
        <DialogTitle>
          Zone Preview{zonePreviewZone ? `: ${zonePreviewZone.name || zonePreviewZone.zone_id}` : ''}
        </DialogTitle>
        <DialogContent>
          {zonePreviewLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!zonePreviewLoading && zonePreviewError && <Alert severity="error">{zonePreviewError}</Alert>}
          {!zonePreviewLoading && !zonePreviewError && zonePreviewImg && (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <img src={zonePreviewImg} alt="Zone preview" style={{ display: 'block', width: '100%', height: 'auto' }} />
              <svg viewBox="0 0 1 1" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                {zoneOverlay?.kind === 'rect' && (
                  <rect
                    x={zoneOverlay.x}
                    y={zoneOverlay.y}
                    width={zoneOverlay.w}
                    height={zoneOverlay.h}
                    fill="rgba(0, 255, 255, 0.15)"
                    stroke="rgba(0, 255, 255, 0.95)"
                    strokeWidth={0.008}
                  />
                )}
                {zoneOverlay?.kind === 'polygon' && (
                  <polygon
                    points={zoneOverlay.points}
                    fill="rgba(0, 255, 255, 0.15)"
                    stroke="rgba(0, 255, 255, 0.95)"
                    strokeWidth={0.008}
                  />
                )}
              </svg>
            </Box>
          )}
          {!zonePreviewLoading && !zonePreviewError && zonePreviewImg && !zoneOverlay && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This zone has no geometry (`bbox_norm` / `points_norm`) available to draw in the preview.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeZonePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deployLintOpen} onClose={closeDeployLint} maxWidth="md" fullWidth>
        <DialogTitle>{deployLintTitle}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Deployment is blocked because one or more policies reference missing/disabled geometry (zones/lines) or invalid fields.
          </Alert>
          <List dense>
            {(deployLintErrors || []).map((error: any, idx: number) => {
              const href = lintFixHref(error);
              const msg = String(error?.message || error?.detail || error?.kind || 'Lint error');
              const metaBits: string[] = [];
              if (error?.policy_id) metaBits.push(`policy_id=${error.policy_id}`);
              if (error?.zone_id) metaBits.push(`zone_id=${error.zone_id}`);
              if (error?.line_id) metaBits.push(`line_id=${error.line_id}`);
              if (error?.camera_id) metaBits.push(`camera_id=${error.camera_id}`);
              return (
                <ListItem key={idx} alignItems="flex-start">
                  <ListItemText
                    primary={msg}
                    secondary={
                      <>
                        {metaBits.length ? (
                          <Typography variant="caption" color="text.secondary">
                            {metaBits.join(' • ')}
                          </Typography>
                        ) : null}
                        {href ? (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            <Link
                              href={href}
                              onClick={(ev) => {
                                ev.preventDefault();
                                openLintFix(href);
                              }}
                            >
                              Fix →
                            </Link>
                          </Typography>
                        ) : null}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeployLint}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clockGateOpen} onClose={closeClockGate} maxWidth="sm" fullWidth>
        <DialogTitle>Device clock not synchronized</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            This policy uses a schedule, but the edge device clock is not synchronized (NTP unsynced). Scheduled rules may run at the wrong times or not at all. Fix the device clock and try again.
          </Typography>
          {clockGatePolicy && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Policy:</strong> {clockGatePolicy}
            </Typography>
          )}
          {clockGateDevices.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Device:</strong> {clockGateDevices.length === 1 ? clockGateDevices[0] : `${clockGateDevices.length} devices`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={openClockPreflight}>
            Open Preflight (Time Check)
          </Button>
          <Button onClick={closeClockGate}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={snackbar.autoHideDuration ?? 6000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PolicyAuxDialogs;
